import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/global.css';
import './styles/list-motion.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { applyCampaignPoojaLandingFastPath, captureUtmFromUrl } from './utils/utm';
import { prefetchInitialRoute } from './utils/prefetchInitialRoute';
import { holdSplashChrome } from './utils/splashChrome';
import {
  shouldSkipSplashThisSession,
  shouldSkipSplashForPath,
  shouldSkipMobileSplashForPerf,
  hideStaticHtmlSplash,
} from './utils/splashSession';
import { installMetaPixelGuard } from './utils/metaPixelGuard';
import { installChunkRecovery } from './utils/chunkRecovery';
import { prefetchActiveLocale } from './i18n/translations';
// import { logMetaPixelDiag } from './utils/metaPixelInit';
// import { logMetaPixelDebug } from './utils/metaPixelDebug';

function runWhenIdle(fn, timeout = 3500) {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => fn(), { timeout });
  } else {
    window.setTimeout(fn, 1);
  }
}

if (typeof window !== 'undefined') {
  installMetaPixelGuard();
  installChunkRecovery();
  prefetchActiveLocale();
  // Third-party inits after first paint — same behavior, later network cost
  runWhenIdle(() => {
    import('./utils/metaPixelInit').then((m) => m.ensureMetaPixelInit());
    import('./utils/plausibleInit').then((m) => m.ensurePlausibleInit());
    import('./utils/openpanelInit').then((m) => m.ensureOpenPanelInit());
    import('./utils/openobserveInit').then((m) => m.ensureOpenObserveInit());
  });
}

// Capture UTMs before React renders. Cubera params are captured in App.jsx (UtmCapture) on every route change.
if (typeof window !== 'undefined') {
  captureUtmFromUrl(window.location.search);
  const campaignLanding = applyCampaignPoojaLandingFastPath();
  if (
    campaignLanding ||
    shouldSkipSplashForPath(window.location.pathname) ||
    shouldSkipMobileSplashForPerf()
  ) {
    hideStaticHtmlSplash();
  }
  prefetchInitialRoute();
  if (
    !campaignLanding &&
    (window.location.pathname === '/' || window.location.pathname === '')
  ) {
    import(
      /* webpackChunkName: "home-hero-boot" */ './utils/homeHeroBootstrap'
    ).then((m) => {
      m.hydrateHomeHeroFromCache();
      m.bootstrapHomeHero();
    });
  }
  const skipSplash =
    shouldSkipSplashThisSession() ||
    shouldSkipSplashForPath(window.location.pathname) ||
    shouldSkipMobileSplashForPerf();
  if (!skipSplash && process.env.REACT_APP_ENABLE_SPLASH !== 'false') {
    holdSplashChrome();
    import(
      /* webpackChunkName: "splash-screen" */
      /* webpackPrefetch: true */
      './components/SplashScreen'
    );
  }
}

/** Build-time pooja pages carry JSON-LD + a text fact sheet for crawlers; once the app is up it renders the
 *  same content itself (and its own JSON-LD), so remove the static copies to avoid duplicates. */
function dropStaticSeoExtras() {
  document.getElementById('seo-prerender')?.remove();
  const staticLd = document.getElementById('pooja-jsonld');
  if (!staticLd) return;
  const appLdReady = () => document.querySelector('script[type="application/ld+json"][data-rh="true"]');
  if (appLdReady()) {
    staticLd.remove();
    return;
  }
  const mo = new MutationObserver(() => {
    if (appLdReady()) {
      staticLd.remove();
      mo.disconnect();
    }
  });
  mo.observe(document.head, { childList: true });
  setTimeout(() => {
    mo.disconnect();
    staticLd.remove();
  }, 15000);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
window.requestAnimationFrame(dropStaticSeoExtras);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
